import React, { useState } from "react";
import { Clock, Trash2, ChevronRight, TreePine, Utensils, Table2, Gift } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { Modal } from "./Modal";
import { formatMoney } from "../lib/format";

const CATS = [
  { key: "decoration", labelKey: "plan.decoration", icon: TreePine, color: "#1B7A4B" },
  { key: "ceia", labelKey: "plan.ceia", icon: Utensils, color: "#C8102E" },
  { key: "table", labelKey: "budget.table", icon: Table2, color: "#8B5CF6" },
  { key: "gifts", labelKey: "nav.gifts", icon: Gift, color: "#D4AF37" },
];

const fmtDate = (iso, lang) => {
  try { return new Date(iso).toLocaleString(lang === "pt-PT" ? "pt-PT" : lang === "en" ? "en-US" : lang === "es" ? "es-ES" : lang === "fr" ? "fr-FR" : lang === "it" ? "it-IT" : "pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export const BudgetHistory = ({ snapshots, symbol, onDelete }) => {
  const { t, lang } = useI18n();
  const [view, setView] = useState(null);
  const fmt = (v) => formatMoney(symbol, v || 0);

  return (
    <div className="glass mt-8 rounded-2xl p-6" data-testid="budget-history">
      <div className="mb-1 flex items-center gap-2">
        <Clock className="h-4 w-4 text-xmas-gold" />
        <h3 className="font-display text-lg text-xmas-snow">{t("bhist.title")}</h3>
        <span className="text-[11px] text-xmas-slate">({snapshots.length}/10)</span>
      </div>
      <p className="mb-4 text-xs text-xmas-slate">{t("bhist.hint")}</p>
      {snapshots.length === 0 ? (
        <p className="text-sm text-xmas-slate" data-testid="budget-history-empty">{t("bhist.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {snapshots.map((s) => (
            <li key={s.snap_id} className="flex items-center gap-3 rounded-xl bg-xmas-navy/40 px-4 py-3" data-testid={`budget-snap-${s.snap_id}`}>
              <button type="button" onClick={() => setView(s)} className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left" data-testid={`budget-snap-open-${s.snap_id}`}>
                <span className="min-w-0">
                  <span className="block font-mono-num text-sm text-xmas-snow">{fmtDate(s.saved_at, lang)}</span>
                  <span className="block text-[11px] text-xmas-slate">{t("bhist.total")}: {fmt(s.total?.limit)} · {t("bhist.planned")}: {fmt(s.total?.planned)}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-xmas-gold" />
              </button>
              <button type="button" onClick={() => onDelete(s.snap_id)} aria-label="delete" data-testid={`budget-snap-delete-${s.snap_id}`} className="shrink-0 rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!view} onClose={() => setView(null)} title={view ? `${t("bhist.title")} · ${fmtDate(view.saved_at, lang)}` : ""} testid="budget-snap-modal">
        {view && (
          <div>
            <div className="mb-4 flex flex-wrap gap-4 text-sm">
              <span className="text-xmas-slate">{t("bhist.total")}: <b className="font-mono-num text-xmas-gold">{fmt(view.total?.limit)}</b></span>
              <span className="text-xmas-slate">{t("bhist.planned")}: <b className="font-mono-num text-xmas-snow">{fmt(view.total?.planned)}</b></span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {CATS.map((c) => {
                const cat = view.categories?.[c.key] || {};
                const saves = view.saves?.[c.key] || [];
                const Icon = c.icon;
                return (
                  <div key={c.key} className="rounded-xl border border-xmas-gold/15 bg-xmas-navy/40 p-4" data-testid={`budget-snap-cat-${c.key}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-semibold text-xmas-snow"><Icon className="h-4 w-4" style={{ color: c.color }} /> {t(c.labelKey)}</span>
                      <span className="font-mono-num text-xs text-xmas-champagne">{fmt(cat.planned)} / {fmt(cat.limit)}</span>
                    </div>
                    {saves.length === 0 ? (
                      <p className="text-xs text-xmas-slate">{t("bhist.no_saves")}</p>
                    ) : saves.map((sv, i) => (
                      <div key={i} className="mt-2 rounded-lg bg-xmas-navycard/70 p-2.5">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="truncate text-xmas-gold">{sv.name || fmtDate(sv.saved_at, lang)}</span>
                          {sv.total != null && <span className="font-mono-num text-xmas-champagne">{fmt(sv.total)}</span>}
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {(sv.items || []).map((it, j) => (
                            <li key={j} className="flex justify-between gap-2 text-[11px] text-xmas-slate">
                              <span className="truncate text-xmas-snow/90">{it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}</span>
                              {it.price != null && <span className="font-mono-num shrink-0">{fmt(Number(it.price) * Math.max(1, it.qty || 1))}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
